/**
 * Loads, heals and translates the recipe behind `recipe-detail/[id]`.
 *
 * Extracted from the screen so the state machine it drives (recipeDetailState)
 * can be tested — `app/` is outside the Jest roots, so nothing that lives in the
 * screen file can have a regression test. See recipeDetailState.ts for why every
 * async result carries the id it was started for (#565).
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import type { Recipe } from '../types/recipe';
import recipeService from '../services/recipeService';
import unifiedRecipeService, { newRecipeReadTrace, type RecipeReadTrace } from '../services/unifiedRecipeService';
import personalRecipeDetails from '../services/personalRecipeDetails';
import { peekDraftRecipe, releaseDraftRecipe } from '../services/draftRecipeHandoff';
import recipeTranslationService from '../services/recipeTranslationService';
import i18n from '../i18n';
import { captureError, trackEvent } from '../services/analyticsService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { getAuthHeaders } from '../services/apiClient';
import { updateMealRecipe } from '../redux/mealPlanSlice';
import { recordViewed } from '../redux/recipeMetadataSlice';
import { selectCurrentLanguage } from '../redux/languageSlice';
import { selectRecipeIndex } from '../redux/recipeIndexSlice';
import {
  isVideoUrl,
  hasRecipeContent,
  isRecipeContentUnavailable,
  shouldScrapeRecipe,
  preferRealSummary,
} from '../utils/recipeSource';
import {
  getLastFailedScrapeAt,
  recordFailedScrape,
  clearFailedScrape,
} from '../utils/scrapeBackoff';
import {
  recipeDetailReducer,
  stateForRecipe,
  shouldTranslateRecipe,
  planRecipeOpen,
  drawsBundledFileFirst,
  sameRecipe,
  isScrapeStatusWorthReporting,
  scrapeErrorTypeForStatus, scrapeErrorTypeForThrown,
  type ContentStatus,
  type RecipeLoadError,
  type ScrapeStatus,
  type RecipeDetailState,
  editableRecipe,
} from './recipeDetailState';
import type { ScrapeErrorType } from '../components/recipe-detail/RecipeDataErrorCard';
import { getApiBaseUrl } from '../config/apiBase';
import { parseRecipeDuration as parseIsoDuration } from '../utils/parseRecipeDuration';

/**
 * What the Firestore part of an open cost, for `recipe_loaded` (#874). Counts
 * `unifiedRecipeService.getRecipeById` only: the legacy fallback after a miss
 * everywhere is not counted. `detail_cache` is `hit` when the recipe was
 * already read, `joined` when a prefetch was reading it (recipePrefetch.ts).
 */
const readTraceProps = (trace: RecipeReadTrace) => ({
  reads: trace.reads,
  round_trips: trace.roundTrips,
  detail_cache: trace.cache ?? 'none',
});

// Emit only the hostname to PostHog — full URLs can contain tracking tokens or
// session params.
export const safeHostname = (url?: string): string => {
  if (!url) return 'unknown';
  try { return new URL(url).hostname; } catch { return 'invalid-url'; }
};

// Decode common HTML entities in recipe text fields
const decodeHtmlEntities = (text: string): string =>
  text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/');

/**
 * The index fields the list screens hand over in a nav param so the page can
 * render a title, image and ingredient list before any network call returns.
 */
export function parseNavParamRecipe(recipeData?: string): Recipe | null {
  if (!recipeData) return null;
  try {
    const parsed = JSON.parse(recipeData);
    return {
      id: parsed.id,
      title: parsed.title ? decodeHtmlEntities(parsed.title) : parsed.title,
      image: parsed.image || '',
      servings: typeof parsed.servings === 'string' ? parseInt(parsed.servings) || 4 : parsed.servings || 4,
      readyInMinutes: parsed.readyInMinutes || 0,
      dishTypes: parsed.dishTypes || [],
      cuisines: parsed.cuisines || [],
      diets: parsed.diets || [],
      // Simple ingredients array from index (not full extendedIngredients)
      ingredients: parsed.ingredients || [],
      // Convert string ingredients to structured format so the page renders
      // them immediately without waiting for the background Firestore/scrape load.
      // If the URL already carries structured extendedIngredients, prefer those.
      extendedIngredients: parsed.extendedIngredients?.length
        ? parsed.extendedIngredients
        : (parsed.ingredients || []).map((name: string, idx: number) => ({
            id: idx,
            original: name,
            name,
            amount: 0,
            unit: '',
          })),
      sourceUrl: parsed.sourceUrl,
      sourceName: parsed.sourceName,
    } as Recipe;
  } catch {
    return null;
  }
}

interface UseRecipeDetailDataParams {
  id: string;
  /** Where the user came from — analytics only. */
  from?: string;
  /** Raw `recipeData` nav param, for instant first render. */
  recipeData?: string;
  /** `draft=1`: a recipe the Add Recipe sheet just wrote and may have handed over (#791). */
  draft?: boolean;
  onToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export interface UseRecipeDetailDataResult {
  recipe: Recipe | null;
  /** The recipe as stored, never a translation: what the editor edits (#792). */
  editableRecipe: Recipe | null;
  loading: boolean;
  error: RecipeLoadError | null;
  contentStatus: ContentStatus;
  scrapeStatus: ScrapeStatus;
  scrapeErrorType: ScrapeErrorType | null;
  isTranslating: boolean;
  showOriginal: boolean;
  /** Re-run the pipeline for the recipe on screen. */
  retry: () => Promise<void>;
  /** Merge a just-saved edit into the recipe on screen. */
  applyLocalEdit: (updates: Partial<Recipe>) => void;
  toggleShowOriginal: () => void;
}

export function useRecipeDetailData({
  id,
  from,
  recipeData,
  draft,
  onToast,
}: UseRecipeDetailDataParams): UseRecipeDetailDataResult {
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const currentLanguage = useSelector(selectCurrentLanguage);
  // Read by the loader, not rendered: whether the household holds its own copy.
  const recipeIndex = useSelector(selectRecipeIndex);
  const recipeIndexRef = useRef(recipeIndex);
  recipeIndexRef.current = recipeIndex;

  // A draft the sheet handed over is the recipe itself: no spinner, no read.
  // Looked up per recipe, and released by the load effect once it is drawn.
  const handedOffDraft = useMemo(() => (draft && id ? peekDraftRecipe(id) : null), [draft, id]);
  const initialRecipe = useMemo(
    () => handedOffDraft ?? parseNavParamRecipe(recipeData),
    [handedOffDraft, recipeData],
  );

  const [state, send] = useReducer(
    recipeDetailReducer,
    undefined,
    (): RecipeDetailState => stateForRecipe(id, initialRecipe),
  );

  // Adjust state during render rather than in an effect, so the very first frame
  // after a tap already shows the recipe that was tapped. Doing it in an effect
  // rendered the previous recipe once before correcting itself — the visible
  // half of #565. This is React's documented "adjust state when a prop changes"
  // pattern: it re-renders immediately, before anything is committed.
  if (state.activeId !== id) {
    send({ type: 'navigate', id, initialRecipe });
  }

  // Every async job below carries `requestId`; the reducer drops it if the user
  // has since opened a different recipe.
  const reportContentUnavailable = useCallback(
    (finalRecipe: Recipe | null, reason: string, requestId: string) => {
      if (!requestId) return;
      send({ type: 'contentUnavailable', requestId });
      if (!isRecipeContentUnavailable(finalRecipe)) return;
      trackEvent('recipe_content_unavailable', {
        recipe_id: requestId,
        reason,
        source_host: safeHostname(finalRecipe?.sourceUrl),
        entry_point: from ?? 'unknown',
      });
    },
    [from],
  );

  const scrapeRecipe = useCallback(async (recipeToScrape: Recipe, requestId: string): Promise<boolean> => {
    send({ type: 'scrapeStarted', requestId });
    const apiBaseUrl = getApiBaseUrl();

    const fail = (errorType: ScrapeErrorType, failureReason: string) => {
      send({ type: 'scrapeFailed', requestId, errorType });
      // Remember the attempt so a cosmetic heal that can never succeed backs off
      // for 24h instead of re-running the whole scrape chain on every open.
      // Not a missing account: that one succeeds the moment the guest signs up.
      if (errorType !== 'sign-in') void recordFailedScrape(requestId);
      trackEvent('recipe_scrape_completed', {
        recipe_id: requestId,
        reason: 'both_sources_empty',
        source_host: safeHostname(recipeToScrape.sourceUrl),
        outcome: 'failed',
        failure_reason: failureReason,
      });
      return false;
    };

    try {
      const apiUrl = `${apiBaseUrl}/api/recipes/scrape`;

      let response: Response;
      try {
        const authHeaders = await getAuthHeaders();
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ url: recipeToScrape.sourceUrl }),
        });
      } catch (error) {
        const errorType = scrapeErrorTypeForThrown(error);
        return fail(errorType, errorType);
      }

      if (!response.ok) {
        const status = response.status;
        const errorType = scrapeErrorTypeForStatus(status);
        // A 4xx here is the scraper reaching a verdict, not a fault — see
        // isScrapeStatusWorthReporting. `recipe_scrape_completed` below still
        // records every failure with its reason.
        if (isScrapeStatusWorthReporting(status)) {
          captureError(new Error(`Scraper failed: ${status}`), { context: 'recipeDetail.scrape' });
        }
        return fail(errorType, errorType);
      }

      const { success, recipe: scrapedRecipe } = await response.json();
      if (!success || !scrapedRecipe) {
        return fail('not-found', 'not-found');
      }

      const scrapedIngredients: string[] = scrapedRecipe.ingredients || [];
      const scrapedInstructions: string[] = scrapedRecipe.instructions || [];

      const scrapedExtendedIngredients = scrapedIngredients.map((ing, index) => ({
        id: index,
        name: ing,
        original: ing,
        amount: 0,
        unit: '',
      }));

      // Transform instruction sections if available
      const instructionSections = scrapedRecipe.instructionSections?.map(
        (section: { name?: string; steps: string[] }) => ({
          name: section.name,
          steps: (section.steps || []).map((step: string, stepIdx: number) => ({
            number: stepIdx + 1,
            step,
          })),
        })
      );

      // Transform ingredient sections if available
      const ingredientGroups = scrapedRecipe.ingredientSections
        ?.filter((s: { name?: string; items: string[] }) => s.name && s.items?.length > 0)
        .map((s: { name?: string; items: string[] }) => ({
          name: s.name!,
          items: s.items,
        }));

      const updatedRecipe = {
        ...recipeToScrape,
        extendedIngredients: scrapedExtendedIngredients,
        instructions: scrapedInstructions.map((step, index) => ({
          number: index + 1,
          step,
        })),
        analyzedInstructions: [{
          name: '',
          steps: scrapedInstructions.map((step, index) => ({
            number: index + 1,
            step,
          })),
        }],
        ...(instructionSections?.length > 0 && { instructionSections }),
        ...(ingredientGroups && ingredientGroups.length > 0 && { ingredientGroups }),
        readyInMinutes:
          parseIsoDuration(scrapedRecipe.totalTime || '') ||
          (parseIsoDuration(scrapedRecipe.prepTime || '') + parseIsoDuration(scrapedRecipe.cookTime || '')),
        preparationMinutes: parseIsoDuration(scrapedRecipe.prepTime || ''),
        cookingMinutes: parseIsoDuration(scrapedRecipe.cookTime || ''),
        servings: scrapedRecipe.recipeYield ? parseInt(scrapedRecipe.recipeYield) || 4 : 4,
        ...(scrapedRecipe.recipeYield && { servingsDisplay: scrapedRecipe.recipeYield }),
        image: scrapedRecipe.image || recipeToScrape.image,
        summary: scrapedRecipe.description || recipeToScrape.summary,
        // Fall back to the existing value instead of overwriting with
        // undefined — a fresh scrape that fills in ingredients/instructions
        // but doesn't happen to find a rating/time must never erase a rating
        // the recipe already had.
        sourceRating: scrapedRecipe.sourceRating ?? recipeToScrape.sourceRating,
        sourceRatingCount: scrapedRecipe.sourceRatingCount ?? recipeToScrape.sourceRatingCount,
        sourceRatingScale: scrapedRecipe.sourceRatingScale ?? recipeToScrape.sourceRatingScale,
      };

      send({ type: 'scrapeSucceeded', requestId, recipe: updatedRecipe });
      // The site answered — drop any backoff so a source that recovered isn't
      // held back by a stamp from before it did.
      void clearFailedScrape(requestId);
      // Keyed by the scraped recipe's own id, so it stays correct — and worth
      // doing — even if the user has moved on to a different recipe.
      dispatch(updateMealRecipe({ recipeId: recipeToScrape.id.toString(), recipe: updatedRecipe }));
      trackEvent('recipe_scrape_completed', {
        recipe_id: requestId,
        reason: 'both_sources_empty',
        source_host: safeHostname(recipeToScrape.sourceUrl),
        outcome: 'success',
        has_image: !!updatedRecipe.image,
        has_source_rating: updatedRecipe.sourceRating != null,
        has_cook_time: (updatedRecipe.readyInMinutes ?? 0) > 0,
      });
      onToast(i18n.t('recipeDetail.recipeUpdated'), 'success');

      // Resolve the canonical string id. Recipes loaded via the legacy
      // recipeService carry a hashed NUMERIC `id` with the real
      // `saved-`/`imported-` string on `_firestoreId`; recipes from the unified
      // service already have the string on `id`. Prefer `_firestoreId` so the
      // persistence branch below routes correctly in both cases.
      const recipeIdStr = (updatedRecipe as { _firestoreId?: string })._firestoreId
        ?? recipeToScrape.id.toString();
      if (recipeIdStr.startsWith('saved-')) {
        // Persist scraped data to user's savedRecipes Firestore document
        if (userId) {
          try {
            await unifiedRecipeService.updateRecipe(userId, recipeIdStr, {
              extendedIngredients: updatedRecipe.extendedIngredients,
              instructions: updatedRecipe.instructions,
              analyzedInstructions: updatedRecipe.analyzedInstructions,
              ...(updatedRecipe.instructionSections && { instructionSections: updatedRecipe.instructionSections }),
              ...(updatedRecipe.ingredientGroups && { ingredientGroups: updatedRecipe.ingredientGroups }),
              readyInMinutes: updatedRecipe.readyInMinutes,
              preparationMinutes: updatedRecipe.preparationMinutes,
              cookingMinutes: updatedRecipe.cookingMinutes,
              servings: updatedRecipe.servings,
              image: updatedRecipe.image,
              summary: updatedRecipe.summary,
              sourceRating: updatedRecipe.sourceRating,
              sourceRatingCount: updatedRecipe.sourceRatingCount,
              sourceRatingScale: updatedRecipe.sourceRatingScale,
              lastScrapedAt: new Date().toISOString(),
            });
          } catch (err) {
            console.warn('Failed to persist scrape to savedRecipes (non-critical):', err);
          }
        }
      } else if (recipeIdStr.startsWith('imported-')) {
        // Persist healed content for imported recipes to whichever scope holds
        // the doc (household/personal savedRecipes or legacy importedRecipes).
        // Without this an imported recipe re-scrapes on EVERY view and never
        // converges — the root of the "missing steps" dead-end in #485. The
        // enrich endpoint can't help here: it gates on shared-catalog / saved
        // membership and 403s for imported docs.
        if (userId) {
          await recipeService.persistHealedRecipe(recipeIdStr, userId, {
            extendedIngredients: updatedRecipe.extendedIngredients,
            instructions: updatedRecipe.instructions,
            analyzedInstructions: updatedRecipe.analyzedInstructions,
            ...(updatedRecipe.instructionSections && { instructionSections: updatedRecipe.instructionSections }),
            ...(updatedRecipe.ingredientGroups && { ingredientGroups: updatedRecipe.ingredientGroups }),
            readyInMinutes: updatedRecipe.readyInMinutes,
            preparationMinutes: updatedRecipe.preparationMinutes,
            cookingMinutes: updatedRecipe.cookingMinutes,
            servings: updatedRecipe.servings,
            image: updatedRecipe.image,
            summary: updatedRecipe.summary,
            sourceUrl: updatedRecipe.sourceUrl,
            sourceRating: updatedRecipe.sourceRating,
            sourceRatingCount: updatedRecipe.sourceRatingCount,
            sourceRatingScale: updatedRecipe.sourceRatingScale,
            lastScrapedAt: new Date().toISOString(),
          });
        }
      } else {
        // Enrich shared catalog recipe — persists the healed description for
        // all users so subsequent views load from Firestore with no re-scrape.
        try {
          const authHeaders = await getAuthHeaders();
          const enrichResponse = await fetch(`${apiBaseUrl}/api/recipes/enrich`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ recipeId: recipeIdStr }),
          });
          if (enrichResponse.ok) {
            const enrichResult = await enrichResponse.json();
            console.log('✅ Recipe enriched:', enrichResult.fieldsUpdated);
          }
        } catch (enrichError) {
          console.warn('Failed to enrich shared recipe (non-critical):', enrichError);
        }
      }
      return true;
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'recipeDetail.scrape' });
      send({ type: 'scrapeFailed', requestId, errorType: 'network' });
      void recordFailedScrape(requestId);
      return false;
    }
  }, [dispatch, onToast, userId]);

  // Fill in the full recipe behind the nav-param placeholder, without a spinner.
  const loadRecipeInBackground = useCallback(async (requestId: string, navParamRecipe: Recipe | null) => {
    if (!requestId) return;
    const startTime = Date.now();
    let loadSource: 'local_file' | 'firestore' | 'legacy' = 'local_file';
    const trace = newRecipeReadTrace();
    // The bundled file, when it was drawn before Firestore answered (#874).
    let drawn: Recipe | null = null;
    let drawnAt: number | null = null;

    try {
      let loaded: Recipe | null = null;

      // Firestore is asked at once, not after the file: the two answer side by side.
      const firestoreRead = isFirebaseEnabled()
        ? unifiedRecipeService.getRecipeById(requestId, userId, trace).catch((err) => {
            console.warn('Firestore failed, using local data:', err);
            return null;
          })
        : Promise.resolve(null);

      // STEP 1: Try local detail file first — fastest and most complete for base catalog
      try {
        loaded = await personalRecipeDetails.getRecipeDetails(requestId);
        if (loaded) loadSource = 'local_file';
      } catch {
        // proceed to Firestore
      }

      // Draw it now; what Firestore adds lands behind it (drawsBundledFileFirst).
      const indexEntry = recipeIndexRef.current.find((entry) => entry.id === requestId);
      if (loaded && drawsBundledFileFirst(indexEntry?.source)) {
        send({ type: 'loaded', requestId, recipe: loaded });
        drawn = loaded;
        drawnAt = Date.now();
      }

      // STEP 2: Try Firestore to supplement or replace if no local file found
      if (isFirebaseEnabled()) {
        try {
          const firestoreData = await firestoreRead;
          if (firestoreData) {
            if (!loaded) {
              // No local file — use Firestore (user-saved recipe or unlisted)
              loaded = firestoreData;
              loadSource = 'firestore';
            } else if ((firestoreData as any).updatedAt) {
              // The user has edited this recipe — the Firestore override is the
              // source of truth. The local catalog file still carries the
              // original import values, so keep it only for fields the
              // override doesn't have.
              const overrides = Object.fromEntries(
                Object.entries(firestoreData).filter(([, v]) =>
                  v !== undefined && v !== null && v !== '' &&
                  !(Array.isArray(v) && v.length === 0)
                )
              );
              loaded = { ...loaded, ...overrides } as Recipe;
              loadSource = 'firestore';
            } else {
              // Have local file — supplement with Firestore for missing fields only
              if (!loaded.instructions?.length && firestoreData.instructions?.length) {
                loaded = { ...loaded, instructions: firestoreData.instructions };
                loadSource = 'firestore';
              }
              if (!loaded.extendedIngredients?.length && firestoreData.extendedIngredients?.length) {
                loaded = { ...loaded, extendedIngredients: firestoreData.extendedIngredients };
              }
              if (!loaded.ingredientGroups?.length && firestoreData.ingredientGroups?.length) {
                loaded = { ...loaded, ingredientGroups: firestoreData.ingredientGroups };
              }
              if (!loaded.instructionSections?.length && firestoreData.instructionSections?.length) {
                loaded = { ...loaded, instructionSections: firestoreData.instructionSections };
              }
              // Adopt the real Firestore description when the local file carries
              // only a title-echo placeholder or no summary at all. Without this
              // the intro paragraph (persisted to Firestore by scrape/enrich)
              // never reached the local-first render path and was silently lost.
              loaded = {
                ...loaded,
                summary: preferRealSummary(loaded.summary, firestoreData.summary, loaded.title),
              };
              // Always take Firestore metadata (rating, lastScrapedAt, sourceUrl)
              // Also prefer Firestore timing — it's the enriched/corrected source of truth;
              // local files carry the original import values which may be incomplete.
              loaded = {
                ...loaded,
                lastScrapedAt: firestoreData.lastScrapedAt ?? loaded.lastScrapedAt,
                sourceRating: firestoreData.sourceRating ?? loaded.sourceRating,
                sourceRatingCount: firestoreData.sourceRatingCount ?? loaded.sourceRatingCount,
                sourceUrl: firestoreData.sourceUrl ?? loaded.sourceUrl,
                readyInMinutes: firestoreData.readyInMinutes ?? loaded.readyInMinutes,
                preparationMinutes: firestoreData.preparationMinutes ?? loaded.preparationMinutes,
                cookingMinutes: firestoreData.cookingMinutes ?? loaded.cookingMinutes,
                // The timeline derives readyInMinutes (#850), so it comes from the same
                // place. The bundled file carries one only as of its last
                // `exportBundledCatalog --times`; the document is newer.
                handsOnMinutes: firestoreData.handsOnMinutes ?? loaded.handsOnMinutes,
                readyInMinutesMin: firestoreData.readyInMinutesMin ?? loaded.readyInMinutesMin,
                timeline: firestoreData.timeline ?? loaded.timeline,
                timelineStatus: firestoreData.timelineStatus ?? loaded.timelineStatus,
              };
            }
          }
        } catch (err) {
          console.warn('Firestore failed, using local data:', err);
        }
      }

      // STEP 3: Legacy recipeService fallback (covers edge cases)
      if (!loaded) {
        loaded = await recipeService.getRecipeById(requestId, userId);
        if (loaded) loadSource = 'legacy';
      }

      if (loaded) {
        if (!drawn || !sameRecipe(drawn, loaded)) {
          send({ type: 'loaded', requestId, recipe: loaded });
        }

        const settledAt = Date.now();
        trackEvent('recipe_loaded', {
          recipe_id: requestId,
          source: loadSource,
          instructions_present: (loaded.instructions?.length ?? 0) > 0,
          ingredients_present: (loaded.extendedIngredients?.length ?? 0) > 0,
          has_image: !!loaded.image,
          has_source_rating: loaded.sourceRating != null,
          has_cook_time: (loaded.readyInMinutes ?? 0) > 0,
          // The wait the cook saw: until the recipe was drawn, which for a
          // bundled file is before Firestore answers. `settled_ms` is the whole load.
          duration_ms: (drawnAt ?? settledAt) - startTime,
          settled_ms: settledAt - startTime,
          drawn_before_firestore: drawn !== null,
          ...readTraceProps(trace),
          entry_point: from ?? 'unknown',
        });

        // Scrape when core content is missing (always) or to heal a missing
        // description (throttled to once/24h, and backed off for 24h after a
        // scrape that came back empty). See shouldScrapeRecipe for the full
        // rationale — this is the gate that keeps the #381/#391/#392 storm
        // fixed while still backfilling legacy recipes that never persisted a summary.
        if (shouldScrapeRecipe(loaded, await getLastFailedScrapeAt(requestId))) {
          trackEvent('recipe_scrape_triggered', {
            recipe_id: requestId,
            reason: 'both_sources_empty',
            source_host: safeHostname(loaded.sourceUrl),
          });
          const scraped = await scrapeRecipe(loaded, requestId);
          if (!scraped) reportContentUnavailable(loaded, 'scrape_failed', requestId);
        } else if (!hasRecipeContent(loaded)) {
          // Settled with no content and no scrape will run (no source URL or video).
          reportContentUnavailable(loaded, 'no_scrape', requestId);
        }
      } else if (navParamRecipe?.sourceUrl && !navParamRecipe.videoUrl && !isVideoUrl(navParamRecipe.sourceUrl)) {
        // Recipe not found in any source — scrape from nav-param sourceUrl
        trackEvent('recipe_scrape_triggered', {
          recipe_id: requestId,
          reason: 'both_sources_empty',
          source_host: safeHostname(navParamRecipe.sourceUrl),
        });
        const scraped = await scrapeRecipe(navParamRecipe, requestId);
        if (!scraped) reportContentUnavailable(navParamRecipe, 'scrape_failed', requestId);
      } else {
        // Not found in any source and nothing scrapeable — genuinely unavailable.
        reportContentUnavailable(navParamRecipe, 'not_found', requestId);
      }
    } catch (err) {
      console.error('Error loading recipe details in background:', err);
    }
  }, [from, reportContentUnavailable, scrapeRecipe, userId]);

  // Full load with a spinner, for opens that carry no nav-param recipe.
  const loadRecipe = useCallback(async (requestId: string) => {
    if (!requestId) return;
    const startTime = Date.now();
    let loadSource: 'firestore' | 'legacy' = 'firestore';
    const trace = newRecipeReadTrace();

    try {
      let loaded: Recipe | null = null;

      // Try unified Firestore service first
      if (isFirebaseEnabled()) {
        try {
          loaded = await unifiedRecipeService.getRecipeById(requestId, userId, trace);
        } catch (err) {
          console.warn('Unified service failed, falling back:', err);
        }
      }

      // Fallback to legacy recipeService
      if (!loaded) {
        loaded = await recipeService.getRecipeById(requestId, userId);
        if (loaded) loadSource = 'legacy';
      }

      // Supplement with local file data if Firestore recipe is missing instructions/ingredients/sourceUrl
      if (loaded && (!loaded.instructions?.length || !loaded.extendedIngredients?.length || !loaded.sourceUrl)) {
        try {
          const localData = await recipeService.getRecipeById(requestId, userId);
          if (localData) {
            if (!loaded.instructions?.length && localData.instructions?.length) {
              loaded = { ...loaded, instructions: localData.instructions };
            }
            if (!loaded.extendedIngredients?.length && localData.extendedIngredients?.length) {
              loaded = { ...loaded, extendedIngredients: localData.extendedIngredients };
            }
            if (!loaded.ingredientGroups?.length && localData.ingredientGroups?.length) {
              loaded = { ...loaded, ingredientGroups: localData.ingredientGroups };
            }
            if (!loaded.instructionSections?.length && localData.instructionSections?.length) {
              loaded = { ...loaded, instructionSections: localData.instructionSections };
            }
            if (!loaded.sourceUrl && localData.sourceUrl) {
              loaded = { ...loaded, sourceUrl: localData.sourceUrl };
            }
          }
        } catch {
          // Non-critical: proceed with Firestore data
        }
      }

      // Last-resort source recovery: a saved/imported recipe that is missing
      // renderable content AND has no sourceUrl can never self-heal (the scrape
      // gate requires a source) — it dead-ends on "Aucune instruction ajoutée"
      // (#485). Try to borrow a sourceUrl (and any instructions) from a
      // shared-catalog recipe with the same title before giving up.
      if (loaded && !loaded.sourceUrl && !hasRecipeContent(loaded) && loaded.title) {
        try {
          const shared = await unifiedRecipeService.getSharedRecipeByTitle(loaded.title);
          if (shared) {
            if (shared.sourceUrl) {
              loaded = { ...loaded, sourceUrl: shared.sourceUrl };
            }
            if (!loaded.instructions?.length && shared.instructions?.length) {
              loaded = { ...loaded, instructions: shared.instructions };
            }
            if (!loaded.instructionSections?.length && shared.instructionSections?.length) {
              loaded = { ...loaded, instructionSections: shared.instructionSections };
            }
          }
        } catch {
          // Non-critical: proceed without recovered source
        }
      }

      if (loaded) {
        send({ type: 'loaded', requestId, recipe: loaded });

        // This path never emitted recipe_loaded, undercounting loads for every
        // open without a nav-param recipe (e.g. taps on a not-yet-rehydrated
        // meal). Opens and loads must pair 1:1 (#496).
        trackEvent('recipe_loaded', {
          recipe_id: requestId,
          source: loadSource,
          instructions_present: (loaded.instructions?.length ?? 0) > 0,
          ingredients_present: (loaded.extendedIngredients?.length ?? 0) > 0,
          has_image: !!loaded.image,
          has_source_rating: loaded.sourceRating != null,
          has_cook_time: (loaded.readyInMinutes ?? 0) > 0,
          duration_ms: Date.now() - startTime,
          ...readTraceProps(trace),
          entry_point: from ?? 'unknown',
        });

        // Live-scrape when the recipe is missing core CONTENT (ingredients or
        // instructions) or to heal a missing description. Missing metadata (time,
        // image, rating) must NEVER trigger a scrape — re-scraping content-complete
        // recipes for metadata caused the failure storm in #381/#391/#392. See
        // shouldScrapeRecipe for the exact gating (skips video recipes, throttles
        // the description heal to once/24h, backs off after a failed scrape).
        if (shouldScrapeRecipe(loaded, await getLastFailedScrapeAt(requestId))) {
          const scraped = await scrapeRecipe(loaded, requestId);
          if (!scraped) reportContentUnavailable(loaded, 'scrape_failed', requestId);
        } else if (!hasRecipeContent(loaded)) {
          // Renderable-content missing but no scrape will run (no source URL,
          // video, or recently scraped) — genuinely unavailable.
          reportContentUnavailable(loaded, 'no_scrape', requestId);
        }
      } else {
        send({ type: 'loadFailed', requestId, error: 'not-found' });
        reportContentUnavailable(null, 'not_found', requestId);
      }
    } catch (err) {
      console.error('Error loading recipe:', err);
      send({ type: 'loadFailed', requestId, error: 'load-failed' });
    }
  }, [from, reportContentUnavailable, scrapeRecipe, userId]);

  // Kick off the load for whichever recipe is now on screen.
  useEffect(() => {
    if (!id) return;
    dispatch(recordViewed(id));

    const plan = planRecipeOpen(initialRecipe, handedOffDraft);

    if (plan === 'draft' && handedOffDraft) {
      // Written by this device a moment ago: nothing to read, nothing to heal,
      // and a blank is not a recipe whose content went missing.
      releaseDraftRecipe(id);
      send({ type: 'loaded', requestId: id, recipe: handedOffDraft });
      trackEvent('recipe_loaded', {
        recipe_id: id,
        source: 'draft',
        instructions_present: (handedOffDraft.instructions?.length ?? 0) > 0,
        ingredients_present: (handedOffDraft.extendedIngredients?.length ?? 0) > 0,
        has_image: !!handedOffDraft.image,
        has_source_rating: false,
        has_cook_time: (handedOffDraft.readyInMinutes ?? 0) > 0,
        duration_ms: 0,
        entry_point: from ?? 'unknown',
      });
      return;
    }

    if (plan === 'load' || !initialRecipe) {
      loadRecipe(id);
      return;
    }

    // Delegates to hasRecipeContent which requires BOTH ingredients AND
    // instructions to be non-empty — a meal-plan snapshot with amount: 0 and
    // instructions: [] is not full data.
    const hasFullDetails = plan === 'embedded';

    trackEvent('recipe_detail_opened', {
      recipe_id: id,
      has_ingredients: (initialRecipe.extendedIngredients?.length ?? 0) > 0,
      has_instructions: (initialRecipe.instructions?.length ?? 0) > 0,
      has_image: !!initialRecipe.image,
      has_source_rating: initialRecipe.sourceRating != null,
      has_cook_time: (initialRecipe.readyInMinutes ?? 0) > 0,
      will_background_load: !hasFullDetails,
      entry_point: from ?? 'unknown',
    });

    if (!hasFullDetails) {
      loadRecipeInBackground(id, initialRecipe);
    } else {
      // Terminal outcome too: the nav-param recipe already carries full
      // content, so no background load will run. Emit recipe_loaded anyway —
      // opens and loads must pair 1:1 for the missing-content telemetry to
      // be trustworthy (#496).
      trackEvent('recipe_loaded', {
        recipe_id: id,
        source: 'embedded',
        instructions_present: true,
        ingredients_present: true,
        has_image: !!initialRecipe.image,
        has_source_rating: initialRecipe.sourceRating != null,
        has_cook_time: (initialRecipe.readyInMinutes ?? 0) > 0,
        duration_ms: 0,
        entry_point: from ?? 'unknown',
      });
    }
    // Deliberately keyed on the recipe alone: the loaders are recreated every
    // render, and re-running this per render would re-fetch and re-scrape.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-translate recipe content when the language doesn't match.
  //
  // `isTranslating` is deliberately absent from the deps: it flips true the
  // moment the translation starts, and re-running would fire this effect's
  // cleanup and cancel the very request it just kicked off.
  //
  // Keyed on the recipe object itself, so every content swap re-runs it. The
  // placeholder → `loaded` swap changes neither the id nor, for a saved recipe,
  // `originalLanguage`, and a translation started before it went on to land
  // over the loaded recipe (#715). The reducer drops such a result too.
  const { recipe, showOriginal } = state;
  useEffect(() => {
    if (!recipe || !shouldTranslateRecipe(state)) return;

    // translateRecipe is an AI round-trip taking seconds — the widest window in
    // which the user can open a different recipe. `cancelled` covers this effect
    // re-running; the reducer's requestId check covers the id (#565).
    const requestId = id;
    let cancelled = false;
    // Whether this run announced a wait that has not been settled yet. A run
    // cancelled by new content settles it on the way out: the run replacing it
    // may never announce one (a device-cache hit), and would leave the spinner up.
    let waiting = false;

    const run = async () => {
      const lang = await recipeTranslationService.detectRecipeLanguage(recipe);
      if (cancelled || !lang || lang === 'unknown' || lang === currentLanguage) return;

      // Every open of an already-translated recipe comes through here, so ask
      // the device cache before announcing a wait. A hit resolves in a
      // microtask: announcing it flashed "Traduction…" for a frame and cost two
      // extra renders of a screen whose render is not cheap.
      const cached = await recipeTranslationService.getCachedRecipeTranslation(
        recipe,
        currentLanguage,
      );
      if (cancelled) return;
      if (cached) {
        send({ type: 'translateSucceeded', requestId, recipe: cached, original: recipe });
        return;
      }

      send({ type: 'translateStarted', requestId });
      waiting = true;
      try {
        // Hand `lang` over rather than letting the service re-derive it from
        // recipe.originalLanguage — that re-derivation discarded this detection
        // and silently returned the recipe untranslated.
        const translated = await recipeTranslationService.translateRecipe(
          recipe,
          currentLanguage,
          lang,
        );
        if (cancelled) return;
        send({ type: 'translateSucceeded', requestId, recipe: translated, original: recipe });
      } catch (err) {
        console.error('Failed to translate recipe:', err);
      } finally {
        if (!cancelled) {
          waiting = false;
          send({ type: 'translateSettled', requestId });
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (waiting) {
        waiting = false;
        send({ type: 'translateSettled', requestId });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, recipe, currentLanguage, showOriginal]);

  const retry = useCallback(async () => {
    if (!id) return;
    // A recipe with a source can be healed by re-scraping; one without can only
    // be re-read.
    if (state.recipe?.sourceUrl) {
      await scrapeRecipe(state.recipe, id);
    } else {
      await loadRecipe(id);
    }
  }, [id, loadRecipe, scrapeRecipe, state.recipe]);

  const applyLocalEdit = useCallback((updates: Partial<Recipe>) => {
    send({ type: 'localEdit', updates });
  }, []);

  const toggleShowOriginal = useCallback(() => {
    send({ type: 'toggleOriginal' });
  }, []);

  return {
    recipe: state.recipe,
    editableRecipe: editableRecipe(state),
    loading: state.loading,
    error: state.error,
    contentStatus: state.contentStatus,
    scrapeStatus: state.scrapeStatus,
    scrapeErrorType: state.scrapeErrorType,
    isTranslating: state.isTranslating,
    showOriginal: state.showOriginal,
    retry,
    applyLocalEdit,
    toggleShowOriginal,
  };
}
